/**
 * ReviewQueuePage — operator review-queue inbox.
 *
 * Converged onto the shared resource framework (#1416): the former 627-line
 * bespoke page (hand-wired table, out-of-table kind Select, inline resolve
 * cell, custom drawer, count badge, 30s refetch) is now
 * `ResourcePage` + `reviewQueueResource`. The disposition-required resolve
 * flow rides the rowActions v2 custom-confirm surface; the 30s cadence rides
 * the `refetchInterval` seam. See `reviewQueueResource.tsx` for the definition.
 *
 * Two tabs since #1998 — PENDING (today's still-open messages) and HISTORY
 * (the full ledger). Each is a separate bundle over the same model, differing
 * in the status view it asks for, its date bound, and its slug; the page holds
 * only which one is showing and mounts ONE `ResourcePage` for it. Mounting
 * both and hiding one would double the polling against a 30s cadence.
 *
 * ## Where the strip sits (#2004)
 *
 * #1998 rendered the strip ABOVE `ResourcePage`, which put it above the page
 * title — the page opened with two buttons and named itself on the second
 * line. That shape was copied from `/audit` and `/webhooks`, and the copy was
 * the mistake: those tabs navigate between SIBLING ROUTES that each carry their
 * own title, so their strip is a layer of sub-navigation and sits above.
 * These two are views of ONE resource on ONE route, so the strip qualifies the
 * title. It travels through `host.listTabs` into the shared pinned masthead;
 * the controlled `Tabs` root remains intact there.
 *
 * ## Tab state lives in the URL
 *
 * `?tab=history`, so a refresh or a pasted link reconstructs the view. A QUERY
 * PARAM and not a path segment, under one rule: a path segment says "a
 * different thing", a query param says "the same thing, seen differently".
 * These two tabs are one table plus a filter. The same rule sends `/webhooks`
 * and `/audit` the other way, to real path segments (#2005).
 *
 * The resource is built IN-COMPONENT (via `useMemo`) so the resolve action can
 * capture the page's `QueryClient` and invalidate the per-order trace views
 * alongside the resource list.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ResourcePage, Tabs, TabsList, TabsTrigger, Badge } from '@resource-kit/ui/react';
import { INTERNAL_QUERY_KEYS } from '@resource-kit/controllers';
import i18n from '@/i18n';
import { usePendingReviewCount } from '@hooks/usePendingReviewCount';
import {
  REVIEW_QUEUE_TAB,
  buildReviewQueueResource,
  type ReviewQueueTab,
} from './reviewQueueResource';

const TAB_LABEL: Record<ReviewQueueTab, string> = {
  [REVIEW_QUEUE_TAB.PENDING]: 'reviewQueue.tab.pending',
  [REVIEW_QUEUE_TAB.HISTORY]: 'reviewQueue.tab.history',
};

// Declaration order IS render order: pending leads because it is the working
// surface; history is where an operator goes to look something up.
const TAB_ORDER: readonly ReviewQueueTab[] = [REVIEW_QUEUE_TAB.PENDING, REVIEW_QUEUE_TAB.HISTORY];

/**
 * The URL key carrying the tab.
 *
 * `_`-prefixed, from the same single definition as `_preset` / `_cleared`: the
 * list machinery treats every non-reserved, non-underscore key as a FILTER
 * FIELD, so a bare `tab=` was decoded as a condition and dropped with a
 * "not accepted by the backend whitelist" warning on every history load. The
 * prefix is what says "page-internal presentation state", which is exactly
 * what a tab is.
 */
const TAB_PARAM = INTERNAL_QUERY_KEYS.tab;

/**
 * The tab a URL asks for. Anything outside the closed set — a typo, a stale
 * link from a renamed tab — reads as the default rather than as an error: the
 * inbox has a perfectly good thing to show, and a 404-ish empty state would
 * punish the reader for a bad link they did not write.
 */
function tabFromParam(raw: string | null): ReviewQueueTab {
  return TAB_ORDER.find((t) => t === raw) ?? REVIEW_QUEUE_TAB.PENDING;
}

export default function ReviewQueuePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tab = tabFromParam(params.get(TAB_PARAM));

  const resource = useMemo(() => buildReviewQueueResource(queryClient, tab), [queryClient, tab]);

  function selectTab(next: ReviewQueueTab) {
    const nextParams = new URLSearchParams(params);
    // The default carries no information, so it does not go in the URL. A URL
    // that spells out the default invites the reader to read it as a choice
    // somebody made, and makes the plain link and the "default" link two
    // different strings for one view.
    if (next === REVIEW_QUEUE_TAB.PENDING) nextParams.delete(TAB_PARAM);
    else nextParams.set(TAB_PARAM, next);
    // `replace` so a session of flipping between the two tabs does not bury the
    // page the operator arrived from under a stack of history entries.
    setParams(nextParams, { replace: true });
  }

  return (
    <ResourcePage
      // Keyed so switching tabs REMOUNTS the resource page: page number, sort
      // and column state belong to the view being left, and carrying e.g.
      // "page 4" into a differently-sized list lands on nothing.
      key={tab}
      bundle={{
        ...resource,
        host: {
          ...resource.host,
          listTabs: () => (
            <Tabs value={tab} onValueChange={(value) => selectTab(value as ReviewQueueTab)}>
              <TabsList aria-label={t('reviewQueue.pageTitle')}>
                {TAB_ORDER.map((key) => (
                  <TabsTrigger key={key} value={key} data-testid={`review-queue-tab-${key}`}>
                    <span className="inline-flex items-center gap-2">
                      {t(TAB_LABEL[key])}
                      {key === REVIEW_QUEUE_TAB.PENDING && <PendingCount />}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          ),
        },
      }}
    />
  );
}

/**
 * The unresolved count, rendered ON the Pending tab.
 *
 * It used to hang off the page title via `listTitleAdornment`, driven by
 * whichever list was showing — so on History the badge reported the size of the
 * archive, which reads as a backlog. The number only ever meant "pending", so
 * it now sits on the tab that means the same thing and reads its OWN one-row
 * query rather than the rendered list's total.
 *
 * That query is `usePendingReviewCount`, shared with the dispatch console's
 * review chip: one hook, one definition of "pending", no way for the two
 * surfaces to disagree.
 */
function PendingCount() {
  const total = usePendingReviewCount().data?.total ?? 0;
  if (total <= 0) return null;
  return (
    <Badge variant="secondary" aria-label={i18n.t('reviewQueue.totalUnresolved')}>
      {total}
    </Badge>
  );
}
